import React, { useCallback, useEffect, useMemo, useState } from "react";
import AppShell from "../components/AppShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { PlayerTag } from "../components/badges";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
    Target,
    User,
    Trophy,
    FloppyDisk,
    Trash,
    PlusCircle,
    Swap,
    ArrowsClockwise,
    Crown,
} from "@phosphor-icons/react";
import {
    createPlan,
    deletePlan,
    getManagerSquad,
    getTopManagers,
    listManagerPlans,
    updatePlan,
    whatIfSwap,
} from "../lib/api";
import TransferTimeline from "../components/TransferTimeline";
import RangeFilter, { passesRange } from "../components/RangeFilter";
import VoteButtons from "../components/VoteButtons";

const STATUS_STYLE = {
    HIT: { bg: "#9EE8B4", text: "#050505", label: "HIT" },
    ON_TRACK: { bg: "#9DB7F5", text: "#050505", label: "ON TRACK" },
    RISK: { bg: "#FF2A2A", text: "#ffffff", label: "RISK" },
    NO_TARGET: { bg: "#3a3a3a", text: "#ffffff", label: "NO TARGET" },
};

const RISK_COLOR = { LOW: "#9EE8B4", MEDIUM: "#FFF3C2", HIGH: "#FF2A2A" };

const STORAGE_KEY = "fa.planner.last_manager_id";

export default function Planner() {
    const [managerId, setManagerId] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
    const [submittedId, setSubmittedId] = useState(null);
    const [squad, setSquad] = useState(null);
    const [loading, setLoading] = useState(false);
    const [topManagers, setTopManagers] = useState(null);
    const [plans, setPlans] = useState([]);
    const [whatIf, setWhatIf] = useState(null); // { player } open dialog
    const [whatIfResult, setWhatIfResult] = useState(null);
    const [whatIfLoading, setWhatIfLoading] = useState(false);
    const [savingPlan, setSavingPlan] = useState(false);
    const [planName, setPlanName] = useState("");
    const [activePlanId, setActivePlanId] = useState(null);
    const [transfers, setTransfers] = useState([]);
    const [priceRange, setPriceRange] = useState({ selectedMin: 3.5, selectedMax: 15.0 });
    const [projRange, setProjRange] = useState({ selectedMin: 0, selectedMax: 60 });

    // Squad summary derived from current squad (no client-side targets — the
    // user's conviction is captured via VoteButtons → backend prediction_votes
    // collection, which auto-applies a ±5 % bias to predicted_points_total).
    const squadSummary = useMemo(() => {
        if (!squad) return null;
        const projected = squad.squad.reduce(
            (s, p) => s + (Number(p.predicted_points_total) || 0), 0,
        );
        const myVotes = squad.squad.filter((p) => p.community_votes?.my_vote).length;
        return {
            projected_total: Math.round(projected * 100) / 100,
            my_votes: myVotes,
            roster_size: squad.squad.length,
        };
    }, [squad]);

    const loadSquad = useCallback(async (id) => {
        if (!id) return;
        setLoading(true);
        setSquad(null);
        try {
            const data = await getManagerSquad({ managerId: id, window: 5 });
            setSquad(data);
            localStorage.setItem(STORAGE_KEY, String(id));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load squad");
        } finally {
            setLoading(false);
        }
    }, []);

    const loadPlans = useCallback(async (id) => {
        if (!id) return;
        try {
            const list = await listManagerPlans(id);
            setPlans(list);
        } catch (e) {
            console.warn("Failed to load saved plans:", e?.response?.data?.detail || e?.message);
        }
    }, []);

    const loadTop = useCallback(async () => {
        try {
            const t = await getTopManagers(1);
            setTopManagers(t);
        } catch (e) {
            console.warn("Failed to load top-50 benchmark:", e?.response?.data?.detail || e?.message);
        }
    }, []);

    useEffect(() => {
        if (submittedId) {
            loadSquad(submittedId);
            loadPlans(submittedId);
            loadTop();
        }
    }, [submittedId, loadSquad, loadPlans, loadTop]);

    const handleSubmit = (e) => {
        e?.preventDefault?.();
        const cleaned = String(managerId).trim();
        if (!/^\d+$/.test(cleaned)) {
            toast.error("Manager ID musí být číslo");
            return;
        }
        setSubmittedId(Number(cleaned));
        setActivePlanId(null);
        setTransfers([]);
    };

    const reloadSquad = useCallback(() => {
        if (submittedId) loadSquad(submittedId);
    }, [submittedId, loadSquad]);

    const handleSavePlan = async () => {
        if (!submittedId) return;
        const name = (planName || "").trim() || `Plan ${new Date().toLocaleDateString()}`;
        setSavingPlan(true);
        try {
            if (activePlanId) {
                const updated = await updatePlan(activePlanId, { name, targets: {}, transfers });
                toast.success(`Plan "${updated.name}" uložen`);
            } else {
                const created = await createPlan({
                    manager_id: submittedId,
                    name,
                    gw_window: 5,
                    targets: {},
                    transfers,
                });
                setActivePlanId(created.id);
                toast.success(`Plan "${created.name}" vytvořen`);
            }
            await loadPlans(submittedId);
        } catch (e) {
            toast.error("Save failed");
        } finally {
            setSavingPlan(false);
        }
    };

    const handleLoadPlan = async (plan) => {
        setActivePlanId(plan.id);
        setPlanName(plan.name);
        setTransfers(Array.isArray(plan.transfers) ? plan.transfers : []);
        toast.success(`Plan "${plan.name}" načten`);
    };

    const handleDeletePlan = async (plan) => {
        if (!window.confirm(`Smazat plán "${plan.name}"?`)) return;
        try {
            await deletePlan(plan.id);
            if (activePlanId === plan.id) {
                setActivePlanId(null);
                setPlanName("");
            }
            await loadPlans(submittedId);
            toast.success("Plan smazán");
        } catch (e) {
            toast.error("Delete failed");
        }
    };

    const openWhatIf = (player) => {
        setWhatIf({ outPlayer: player, inId: "" });
        setWhatIfResult(null);
    };

    const runWhatIf = async () => {
        if (!whatIf?.outPlayer || !whatIf?.inId) return;
        setWhatIfLoading(true);
        try {
            const res = await whatIfSwap(submittedId, {
                out_id: whatIf.outPlayer.fpl_id,
                in_id: Number(whatIf.inId),
                window: 5,
                targets: {},
            });
            setWhatIfResult(res);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "What-if failed");
        } finally {
            setWhatIfLoading(false);
        }
    };

    return (
        <AppShell>
            <div className="space-y-6" data-testid="planner-page">
                {/* Header */}
                <div className="flex items-end justify-between flex-wrap gap-4">
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-widest text-fa-yellow">
                            Phase D · Squad Planner v2
                        </div>
                        <h1 className="font-display font-black text-3xl uppercase tracking-tight mt-1">
                            Targets &amp; Transfers
                        </h1>
                        <p className="text-white/50 text-sm mt-1 max-w-xl">
                            Vote on each player's projection and benchmark your team against
                            the top-50 overall managers. The community's conviction feeds
                            back into the model (±5 % cap).
                        </p>
                    </div>
                </div>

                {/* Manager input */}
                <form
                    onSubmit={handleSubmit}
                    className="flex items-center gap-3 flex-wrap border border-white/10 bg-black/40 p-4"
                    data-testid="manager-form"
                >
                    <Label className="font-mono text-[10px] uppercase tracking-widest text-white/50">
                        Manager ID
                    </Label>
                    <Input
                        type="text"
                        inputMode="numeric"
                        value={managerId}
                        onChange={(e) => setManagerId(e.target.value)}
                        placeholder="e.g. 1234567"
                        className="bg-black/40 border-white/10 rounded-none font-mono text-xs w-44"
                        data-testid="manager-id-input"
                    />
                    <Button
                        type="submit"
                        disabled={loading}
                        data-testid="load-squad-btn"
                        className="bg-fa-yellow hover:bg-fa-yellow/90 text-black rounded-none font-mono text-xs uppercase"
                    >
                        <User size={14} weight="bold" className="mr-2" />
                        {loading ? "Loading…" : "Load Squad"}
                    </Button>
                    {squad && (
                        <span className="font-mono text-[11px] text-white/50">
                            FPL ID lookup → my-team page on fantasy.premierleague.com
                        </span>
                    )}
                </form>

                {/* Manager card + top benchmark */}
                {squad && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        <ManagerCard squad={squad} />
                        <SquadSummaryCard summary={squadSummary} squad={squad} />
                        <TopBenchCard top={topManagers} squad={squad} />
                    </div>
                )}

                {/* Squad table */}
                {squad && (
                    <div className="space-y-3" data-testid="squad-section">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="squad-filters">
                            <RangeFilter
                                id="squad-price"
                                testid="squad-price-filter"
                                label="Price"
                                minValue={3.5}
                                maxValue={15.0}
                                step={0.1}
                                selectedMin={priceRange.selectedMin}
                                selectedMax={priceRange.selectedMax}
                                format="currency"
                                onChange={setPriceRange}
                                defaults={{ selectedMin: 3.5, selectedMax: 15.0 }}
                            />
                            <RangeFilter
                                id="squad-projected"
                                testid="squad-projected-filter"
                                label="Projected points"
                                minValue={0}
                                maxValue={60}
                                step={1}
                                selectedMin={projRange.selectedMin}
                                selectedMax={projRange.selectedMax}
                                format="points"
                                onChange={setProjRange}
                                defaults={{ selectedMin: 0, selectedMax: 60 }}
                            />
                        </div>
                        <SquadTable
                            squad={squad}
                            onWhatIf={openWhatIf}
                            onVote={reloadSquad}
                            priceFilter={{ ...priceRange, isEnabled: true }}
                            projFilter={{ ...projRange, isEnabled: true }}
                        />
                    </div>
                )}

                {/* Transfer Timeline */}
                {submittedId && squad && (
                    <TransferTimeline
                        managerId={submittedId}
                        squad={squad}
                        transfers={transfers}
                        setTransfers={setTransfers}
                    />
                )}

                {/* Plan save / list */}
                {submittedId && (
                    <PlansPanel
                        plans={plans}
                        activePlanId={activePlanId}
                        planName={planName}
                        setPlanName={setPlanName}
                        onSave={handleSavePlan}
                        onLoad={handleLoadPlan}
                        onDelete={handleDeletePlan}
                        saving={savingPlan}
                    />
                )}
            </div>

            {whatIf && (
                <WhatIfDialog
                    outPlayer={whatIf.outPlayer}
                    inId={whatIf.inId}
                    setInId={(v) => setWhatIf((prev) => ({ ...prev, inId: v }))}
                    onRun={runWhatIf}
                    loading={whatIfLoading}
                    result={whatIfResult}
                    onClose={() => {
                        setWhatIf(null);
                        setWhatIfResult(null);
                    }}
                />
            )}
        </AppShell>
    );
}

// -- sub-components ---------------------------------------------------------

function ManagerCard({ squad }) {
    return (
        <div className="border border-white/10 bg-black/40 p-4" data-testid="manager-card">
            <div className="flex items-center gap-2 mb-3">
                <Crown size={16} weight="fill" className="text-fa-yellow" />
                <span className="font-mono text-[10px] uppercase tracking-widest text-white/50">
                    Team
                </span>
            </div>
            <div className="font-display font-black text-2xl tracking-tight" data-testid="manager-name">
                {squad.manager_name}
            </div>
            <div className="font-mono text-xs text-white/60 mt-1">
                {squad.player_first_name} {squad.player_last_name}
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 font-mono text-xs">
                <Stat label="Rank" value={squad.overall_rank?.toLocaleString() || "—"} />
                <Stat label="Points" value={squad.overall_points || "—"} />
                <Stat label="Value" value={`£${squad.value?.toFixed(1)}m`} />
            </div>
            <div className="mt-1 grid grid-cols-3 gap-2 font-mono text-xs">
                <Stat label="Bank" value={`£${squad.bank?.toFixed(1)}m`} />
                <Stat label="GW" value={squad.gameweek} />
                <Stat label="Window" value={squad.window} />
            </div>
        </div>
    );
}

function SquadSummaryCard({ summary, squad }) {
    if (!summary) return null;
    const qual = squad?.qualification_summary;
    return (
        <div className="border border-white/10 bg-black/40 p-4" data-testid="squad-summary-card">
            <div className="flex items-center gap-2 mb-3">
                <Target size={16} weight="bold" className="text-fa-yellow" />
                <span className="font-mono text-[10px] uppercase tracking-widest text-white/50">
                    Squad Outlook
                </span>
            </div>
            <div className="grid grid-cols-2 gap-2 font-mono text-xs">
                <Stat label="Projected total" value={summary.projected_total} testid="summary-projected" />
                <Stat label="My votes" value={`${summary.my_votes} / ${summary.roster_size}`} testid="summary-my-votes" />
            </div>
            {qual && (
                <div className="mt-3 flex flex-wrap gap-2 font-mono text-[10px]" data-testid="squad-qual-summary">
                    <span className="px-1.5 py-0.5 border" style={{ color: "#FF6B6B", borderColor: "#FF6B6B66" }}>
                        MUST_WIN {qual.must_win}
                    </span>
                    <span className="px-1.5 py-0.5 border" style={{ color: "#9EE8B4", borderColor: "#9EE8B466" }}>
                        LOCKED {qual.locked}
                    </span>
                    <span className="px-1.5 py-0.5 border" style={{ color: "#FFD180", borderColor: "#FFD18066" }}>
                        ROTATION_RISK {qual.rotation_risk}
                    </span>
                </div>
            )}
            <div className="mt-3 font-mono text-[10px] text-white/40 leading-snug">
                Projection already reflects community conviction (±5 % cap). Vote on each
                player below to express how much you trust the model.
            </div>
        </div>
    );
}

function TopBenchCard({ top, squad }) {
    if (!top) return null;
    const myEvent = squad?.entry_history?.points || squad?.overall_points;
    return (
        <div className="border border-white/10 bg-black/40 p-4" data-testid="top-bench-card">
            <div className="flex items-center gap-2 mb-3">
                <Trophy size={16} weight="fill" className="text-fa-yellow" />
                <span className="font-mono text-[10px] uppercase tracking-widest text-white/50">
                    Top Managers (sample {top.sample_size})
                </span>
            </div>
            <div className="grid grid-cols-2 gap-2 font-mono text-xs">
                <Stat label="Avg total" value={top.avg_total} />
                <Stat label="Avg event" value={top.avg_event} />
                <Stat label="Top total" value={top.top_total} color="#9EE8B4" />
                <Stat label="My total" value={squad?.overall_points} color={
                    myEvent && top.avg_total && squad?.overall_points >= top.avg_total ? "#9EE8B4" : "#FF8800"
                } />
            </div>
            <div className="mt-3 space-y-1">
                {(top.managers || []).slice(0, 3).map((m) => (
                    <div key={m.entry} className="font-mono text-[11px] text-white/60 flex justify-between">
                        <span>#{m.rank} {m.name}</span>
                        <span className="text-fa-yellow">{m.total}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function Stat({ label, value, color, testid }) {
    return (
        <div className="border border-white/5 px-2 py-1" data-testid={testid}>
            <div className="text-[9px] uppercase tracking-widest text-white/40">{label}</div>
            <div className="text-base" style={color ? { color } : undefined}>
                {value}
            </div>
        </div>
    );
}

function StatusChip({ value }) {
    const s = STATUS_STYLE[value] || STATUS_STYLE.NO_TARGET;
    return (
        <span
            data-testid={`status-${value}`}
            className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 inline-block"
            style={{ backgroundColor: s.bg, color: s.text }}
        >
            {s.label}
        </span>
    );
}

function SquadTable({ squad, onWhatIf, onVote, priceFilter, projFilter }) {
    const filteredPlayers = squad.squad.filter((p) => {
        if (!passesRange(p.price, priceFilter)) return false;
        // Treat unranked (null/undefined) projections as pass-through so bench
        // fillers outside the prediction pool aren't silently hidden.
        if (p.predicted_points_total == null) return true;
        return passesRange(p.predicted_points_total, projFilter);
    });
    return (
        <div className="border border-white/10 overflow-x-auto" data-testid="squad-table">
            <div className="bg-black/30 border-b border-white/5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-white/45">
                {filteredPlayers.length} of {squad.squad.length} squad players shown
            </div>
            <table className="w-full text-sm">
                <thead>
                    <tr className="bg-white/5 border-b border-white/10 text-left">
                        <Th>Player</Th>
                        <Th>Team</Th>
                        <Th>Pos</Th>
                        <Th>Price</Th>
                        <Th>Projected</Th>
                        <Th>
                            <span title="Tell us if you think the model is too high (Lower) or too low (Higher).">
                                Conviction
                            </span>
                        </Th>
                        <Th>Risk</Th>
                        <Th>Tags</Th>
                        <Th>Action</Th>
                    </tr>
                </thead>
                <tbody>
                    {filteredPlayers.map((p) => (
                        <tr
                            key={p.fpl_id}
                            className={`border-b border-white/5 hover:bg-white/[0.03] ${p.is_captain ? "bg-fa-yellow/5" : ""
                                }`}
                            data-testid={`squad-row-${p.fpl_id}`}
                        >
                            <Td>
                                <div className="font-display font-bold flex items-center gap-1">
                                    {p.is_captain && <Crown size={12} weight="fill" className="text-fa-yellow" />}
                                    {p.is_vice_captain && !p.is_captain && (
                                        <span className="text-fa-yellow font-mono text-[9px]">VC</span>
                                    )}
                                    {p.web_name}
                                </div>
                                <div className="text-white/40 text-[10px]">
                                    {p.first_name} {p.second_name}
                                </div>
                            </Td>
                            <Td className="font-mono text-xs">{p.team_short}</Td>
                            <Td className="font-mono text-xs">{p.position}</Td>
                            <Td className="font-mono text-xs">£{p.price?.toFixed?.(1) ?? "—"}</Td>
                            <Td className="font-mono text-xs">
                                {p.predicted_points_total != null ? p.predicted_points_total.toFixed(2) : "—"}
                            </Td>
                            <Td>
                                <VoteButtons
                                    playerId={p.fpl_id}
                                    gwWindow={squad.window || 5}
                                    votes={p.community_votes}
                                    onChange={onVote}
                                    predictedAtCast={p.predicted_points_total}
                                    targetGwStart={squad.gameweek}
                                />
                            </Td>
                            <Td className="font-mono text-[10px]" style={{ color: RISK_COLOR[p.risk_level] || "#888" }}>
                                {p.risk_level || "—"}
                            </Td>
                            <Td>
                                <div className="flex flex-wrap gap-1">
                                    {(p.tags || []).slice(0, 2).map((t) => (
                                        <PlayerTag key={t} tag={t} />
                                    ))}
                                </div>
                            </Td>
                            <Td>
                                <button
                                    type="button"
                                    onClick={() => onWhatIf(p)}
                                    data-testid={`whatif-btn-${p.fpl_id}`}
                                    className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 border border-white/20 hover:border-fa-yellow hover:text-fa-yellow transition-colors flex items-center gap-1"
                                >
                                    <Swap size={11} weight="bold" />
                                    What-if
                                </button>
                            </Td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function PlansPanel({ plans, activePlanId, planName, setPlanName, onSave, onLoad, onDelete, saving }) {
    return (
        <div className="border border-white/10 bg-black/40 p-4" data-testid="plans-panel">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
                <div className="flex items-center gap-2">
                    <FloppyDisk size={16} weight="bold" className="text-fa-yellow" />
                    <span className="font-mono text-[10px] uppercase tracking-widest text-white/50">
                        Saved Plans ({plans.length})
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <Input
                        type="text"
                        value={planName}
                        onChange={(e) => setPlanName(e.target.value)}
                        placeholder={activePlanId ? "Update name…" : "New plan name…"}
                        className="bg-black/40 border-white/10 rounded-none font-mono text-xs h-8 w-56"
                        data-testid="plan-name-input"
                    />
                    <Button
                        onClick={onSave}
                        disabled={saving}
                        data-testid="save-plan-btn"
                        className="bg-fa-yellow hover:bg-fa-yellow/90 text-black rounded-none font-mono text-xs uppercase h-8"
                    >
                        {activePlanId ? <ArrowsClockwise size={12} weight="bold" className="mr-1" /> : <PlusCircle size={12} weight="bold" className="mr-1" />}
                        {saving ? "Saving…" : activePlanId ? "Update" : "Save new"}
                    </Button>
                </div>
            </div>
            {plans.length === 0 ? (
                <div className="font-mono text-[11px] text-white/40 py-4 text-center">
                    No saved plans yet. Build a transfer plan above and hit Save.
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {plans.map((p) => (
                        <div
                            key={p.id}
                            className={`border p-3 ${activePlanId === p.id
                                ? "border-fa-yellow/50 bg-fa-yellow/5"
                                : "border-white/10 hover:border-white/30"
                                }`}
                            data-testid={`plan-card-${p.id}`}
                        >
                            <div className="font-display font-bold text-sm">{p.name}</div>
                            <div className="font-mono text-[10px] text-white/40 mt-1">
                                {Object.keys(p.targets || {}).length} targets · {(p.transfers || []).length} transfers · gw_window {p.gw_window}
                            </div>
                            <div className="font-mono text-[10px] text-white/30 mt-1">
                                {new Date(p.updatedAt).toLocaleString()}
                            </div>
                            <div className="flex gap-2 mt-2">
                                <button
                                    type="button"
                                    onClick={() => onLoad(p)}
                                    data-testid={`plan-load-${p.id}`}
                                    className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 border border-white/20 hover:border-fa-yellow hover:text-fa-yellow transition-colors"
                                >
                                    Load
                                </button>
                                <button
                                    type="button"
                                    onClick={() => onDelete(p)}
                                    data-testid={`plan-delete-${p.id}`}
                                    className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 border border-white/20 hover:border-red-500 hover:text-red-500 transition-colors"
                                >
                                    <Trash size={11} weight="bold" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function WhatIfDialog({ outPlayer, inId, setInId, onRun, loading, result, onClose }) {
    return (
        <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="bg-[#0a0a0a] border-white/10 rounded-none max-w-lg" data-testid="whatif-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display uppercase tracking-tight text-xl">
                        What-if · Replace {outPlayer.web_name}
                    </DialogTitle>
                    <DialogDescription className="text-white/50 font-mono text-xs">
                        Simulate replacing this player with another from the prediction pool.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-2">
                    <div className="border border-white/10 p-3">
                        <div className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1">OUT</div>
                        <div className="font-display font-bold">{outPlayer.web_name}</div>
                        <div className="font-mono text-[11px] text-white/40">
                            £{outPlayer.price?.toFixed?.(1)}m · projected{" "}
                            {outPlayer.predicted_points_total != null ? outPlayer.predicted_points_total.toFixed(2) : "—"}
                        </div>
                    </div>
                    <div>
                        <Label className="font-mono text-[10px] uppercase tracking-widest text-white/50">
                            Incoming player ID (FPL element id from /predictions)
                        </Label>
                        <Input
                            type="number"
                            value={inId}
                            onChange={(e) => setInId(e.target.value)}
                            placeholder="e.g. 430 (Haaland)"
                            data-testid="whatif-in-id"
                            className="mt-1 bg-black/40 border-white/10 rounded-none font-mono text-xs"
                        />
                        <div className="font-mono text-[10px] text-white/30 mt-1">
                            Tip: open /predictions in another tab and copy the FPL id of the target player.
                        </div>
                    </div>
                    <Button
                        onClick={onRun}
                        disabled={loading || !inId}
                        data-testid="whatif-run-btn"
                        className="w-full bg-fa-yellow hover:bg-fa-yellow/90 text-black rounded-none font-mono text-xs uppercase"
                    >
                        {loading ? "Simulating…" : "Run simulation"}
                    </Button>

                    {result?.simulation && (
                        <div className="border border-fa-yellow/40 bg-fa-yellow/5 p-3" data-testid="whatif-result">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <div className="font-mono text-[10px] uppercase tracking-widest text-white/50">OUT</div>
                                    <div className="font-display font-bold">{result.simulation.out.name}</div>
                                    <div className="font-mono text-[11px] text-white/60">
                                        £{result.simulation.out.price?.toFixed?.(1)}m · {result.simulation.out.predicted?.toFixed?.(2)}
                                    </div>
                                </div>
                                <div>
                                    <div className="font-mono text-[10px] uppercase tracking-widest text-white/50">IN</div>
                                    <div className="font-display font-bold">{result.simulation.in.name}</div>
                                    <div className="font-mono text-[11px] text-white/60">
                                        £{result.simulation.in.price?.toFixed?.(1)}m · {result.simulation.in.predicted?.toFixed?.(2)}
                                    </div>
                                </div>
                            </div>
                            <div className="mt-3 grid grid-cols-3 gap-2 font-mono text-xs">
                                <Stat
                                    label="Δ Points"
                                    value={result.simulation.points_delta >= 0 ? `+${result.simulation.points_delta}` : result.simulation.points_delta}
                                    color={result.simulation.points_delta >= 0 ? "#9EE8B4" : "#FF2A2A"}
                                    testid="whatif-points-delta"
                                />
                                <Stat
                                    label="Δ Price"
                                    value={result.simulation.price_delta >= 0 ? `+£${result.simulation.price_delta}m` : `£${result.simulation.price_delta}m`}
                                    color={result.simulation.price_delta <= 0 ? "#9EE8B4" : "#FF8800"}
                                />
                                <Stat
                                    label="Verdict"
                                    value={result.simulation.verdict}
                                    color={
                                        result.simulation.verdict === "BUY" ? "#9EE8B4"
                                            : result.simulation.verdict === "AVOID" ? "#FF2A2A"
                                                : "#FFF3C2"
                                    }
                                    testid="whatif-verdict"
                                />
                            </div>
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={onClose}
                        data-testid="whatif-close"
                        className="bg-transparent border-white/20 hover:bg-white/5 rounded-none font-mono text-xs uppercase"
                    >
                        Close
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

const Th = ({ children }) => (
    <th className="font-mono text-[10px] uppercase tracking-widest text-white/50 px-3 py-2">
        {children}
    </th>
);

const Td = ({ children, className = "", style }) => (
    <td className={`px-3 py-2 align-top ${className}`} style={style}>{children}</td>
);
